import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Users, UserPlus, Loader2, Check, X, ShieldCheck } from "lucide-react";

/**
 * TeamAssignmentPanel — Feb 2026
 * Renders the 5-slot project team panel used by CRM & IMOS on:
 *   • Design Project detail
 *   • IMOS Order detail
 *   • Site / Execution Project detail
 *
 * Props:
 *   projectId — design_project id (execution/imos rows expose this as `project_id`)
 *   onChanged — optional callback fired after a successful reassignment
 */
export default function TeamAssignmentPanel({ projectId, onChanged }) {
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [editing, setEditing] = useState(null); // slot key currently in edit mode

  const load = useCallback(async () => {
    if (!projectId) return;
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get(`/projects/${projectId}/team`);
      setState({ loading: false, data, error: null });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Silent 403 — some viewers (e.g. Interior Designer) legitimately don't see the panel
      if (err?.response?.status === 403) {
        setState({ loading: false, data: null, error: "forbidden" });
      } else {
        setState({ loading: false, data: null, error: formatApiErrorDetail(detail) });
      }
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  if (state.loading) {
    return (
      <div className="iz-card p-5" data-testid="team-panel-loading">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-2">Team</p>
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading assignments…
        </div>
      </div>
    );
  }
  if (state.error === "forbidden") return null;
  if (state.error) {
    return (
      <div className="iz-card p-5 border-red-500/30" data-testid="team-panel-error">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-red-400 mb-2">Team</p>
        <p className="text-xs text-red-400">{state.error}</p>
      </div>
    );
  }
  if (!state.data) return null;

  const { slots, can_edit, eligible_users } = state.data;

  const saveSlot = async (slotKey, newUserId) => {
    try {
      await api.post(`/crm/design-projects/${projectId}/reassign`, {
        [slotKey]: newUserId ?? "",
      });
      toast.success("Team assignment updated");
      setEditing(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div className="iz-card p-5" data-testid="team-assignment-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#d4af37]" strokeWidth={1.5} />
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
            Project Team
          </p>
        </div>
        {can_edit ? (
          <span className="text-[9px] font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/30 bg-emerald-500/5 px-2 py-0.5 rounded flex items-center gap-1"
                data-testid="team-can-edit-badge">
            <ShieldCheck className="w-3 h-3" /> Editable
          </span>
        ) : (
          <span className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border border-[#262626] px-2 py-0.5 rounded"
                data-testid="team-read-only-badge">
            Read-only
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {slots.map((s) => (
          <TeamSlotRow
            key={s.key}
            slot={s}
            canEdit={can_edit}
            editing={editing === s.key}
            eligibleUsers={eligible_users?.[s.role_name] || []}
            onStartEdit={() => setEditing(s.key)}
            onCancel={() => setEditing(null)}
            onSave={(uid) => saveSlot(s.key, uid)}
          />
        ))}
      </div>
    </div>
  );
}

function TeamSlotRow({ slot, canEdit, editing, eligibleUsers, onStartEdit, onCancel, onSave }) {
  const [pickedId, setPickedId] = useState(slot.user_id || "");
  useEffect(() => { setPickedId(slot.user_id || ""); }, [slot.user_id, editing]);
  const assigned = !!slot.user_name;

  if (editing) {
    return (
      <div className="border border-[#d4af37]/40 bg-[#0a0a0a] rounded p-3 space-y-2"
           data-testid={`team-slot-edit-${slot.key}`}>
        <p className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">
          {slot.label}
        </p>
        <select
          className="iz-input w-full text-sm"
          value={pickedId}
          onChange={(e) => setPickedId(e.target.value)}
          data-testid={`team-slot-select-${slot.key}`}
        >
          <option value="">— Unassign —</option>
          {eligibleUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.role_label
                ? `${u.name} · ${u.role_label} · ${u.email}`
                : `${u.name} · ${u.email}`}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <button
            className="iz-btn-primary text-[11px] flex items-center gap-1 flex-1 justify-center"
            onClick={() => onSave(pickedId || null)}
            data-testid={`team-slot-save-${slot.key}`}
          >
            <Check className="w-3.5 h-3.5" /> Save
          </button>
          <button
            className="iz-btn-secondary text-[11px] flex items-center gap-1"
            onClick={onCancel}
            data-testid={`team-slot-cancel-${slot.key}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {eligibleUsers.length === 0 && (
          <p className="text-[10px] text-gray-500">No active {slot.role_name}s available.</p>
        )}
      </div>
    );
  }

  return (
    <div
      className={`border rounded p-3 flex items-start justify-between gap-2 ${
        assigned ? "border-[#262626] bg-[#050505]" : "border-[#262626] bg-[#050505]"
      }`}
      data-testid={`team-slot-${slot.key}`}
    >
      <div className="min-w-0">
        <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
          {slot.label}
        </p>
        {assigned ? (
          <>
            <p className="text-sm text-white mt-0.5 truncate" data-testid={`team-slot-name-${slot.key}`}>
              {slot.user_name}
              {slot.user_role && slot.user_role !== slot.role_name && (
                <span className="ml-1.5 text-[10px] font-mono uppercase tracking-widest text-[#d4af37]">
                  · {slot.user_role}
                </span>
              )}
            </p>
            {slot.user_email && (
              <p className="text-[10px] text-gray-500 truncate">{slot.user_email}</p>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500 italic mt-0.5" data-testid={`team-slot-empty-${slot.key}`}>
            Not Assigned
          </p>
        )}
      </div>
      {canEdit && slot.editable !== false && (
        <button
          onClick={onStartEdit}
          className="iz-btn-secondary text-[10px] flex items-center gap-1 shrink-0 border-[#d4af37]/40 text-[#d4af37]"
          data-testid={`team-slot-edit-btn-${slot.key}`}
          title={assigned ? "Reassign" : "Assign"}
        >
          <UserPlus className="w-3 h-3" />
          {assigned ? "Reassign" : "Assign"}
        </button>
      )}
      {canEdit && slot.editable === false && (
        <span className="text-[9px] font-mono uppercase tracking-widest text-gray-500 border border-[#262626] px-1.5 py-0.5 rounded shrink-0"
              data-testid={`team-slot-derived-${slot.key}`}
              title="Derived from IMOS orders — edit in IMOS workspace">
          Auto
        </span>
      )}
    </div>
  );
}
